/**
 * MagneticButton — the call-to-action button. On web with a fine pointer it
 * leans toward the cursor (magnetic), a light sweep crosses it on hover, the
 * label rolls up to a fresh copy and the arrow nudges forward; it dips on
 * press. Everything is CSS on [data-cta]; JS only writes the pull offset into
 * CSS variables. Touch, native and reduced motion: a PressableScale with the
 * same look and no movement.
 *
 *   <MagneticButton label="Get Pro" onPress={...} />
 *   <MagneticButton label="Search" icon="search" arrow={false} variant="primary" />
 *   <MagneticButton label="See plans" variant="ghost" />
 *
 * Variants: primary (edge-line green), light (near-white), ghost (outline).
 */
import React, { useEffect, useRef } from 'react';
import { View, Text, Pressable, ActivityIndicator, Platform, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import PressableScale from './PressableScale';
import { IS_WEB, useReducedMotion } from '../../utils/motion';
import injectCss from '../../utils/injectCss';

const CSS = `
[data-cta] { position: relative; overflow: hidden; isolation: isolate; cursor: pointer; outline: none;
  transform: translate3d(var(--bo-tx, 0px), var(--bo-ty, 0px), 0);
  transition: transform 650ms cubic-bezier(0.16,1,0.3,1), box-shadow 360ms cubic-bezier(0.2,0,0,1), background-color 240ms cubic-bezier(0.2,0,0,1), border-color 240ms cubic-bezier(0.2,0,0,1); }
[data-cta]:focus-visible { outline: 2px solid #A5B4FC; outline-offset: 3px; }
[data-cta][aria-disabled="true"] { cursor: default; }
[data-cta-shine] { position: absolute; top: -20%; bottom: -20%; left: 0; width: 42%; z-index: 0; pointer-events: none; opacity: 0;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.55), transparent); transform: translateX(-130%) skewX(-18deg); }
[data-cta="ghost"] [data-cta-shine] { background: linear-gradient(100deg, transparent, rgba(255,255,255,0.16), transparent); }
[data-cta-roll] { position: relative; overflow: hidden; z-index: 1; }
[data-cta-label] { display: block; transition: transform 520ms cubic-bezier(0.7,0,0.2,1); }
[data-cta-label="b"] { position: absolute; left: 0; right: 0; top: 0; transform: translateY(115%); }
[data-cta-arrow], [data-cta-icon] { position: relative; z-index: 1; transition: transform 520ms cubic-bezier(0.34,1.4,0.64,1); }
@media (hover: hover) and (pointer: fine) {
  [data-cta]:not([aria-disabled="true"]):hover { transition-duration: 200ms, 360ms, 240ms, 240ms; }
  [data-cta]:not([aria-disabled="true"]):hover [data-cta-shine] { animation: boCtaShine 900ms cubic-bezier(0.2,0,0,1) forwards; }
  [data-cta]:not([aria-disabled="true"]):hover [data-cta-label="a"] { transform: translateY(-115%); }
  [data-cta]:not([aria-disabled="true"]):hover [data-cta-label="b"] { transform: translateY(0); }
  [data-cta]:not([aria-disabled="true"]):hover [data-cta-arrow] { transform: translateX(5px); }
  [data-cta]:not([aria-disabled="true"]):hover [data-cta-icon] { transform: rotate(-12deg) scale(1.12); }
  [data-cta="primary"]:not([aria-disabled="true"]):hover { box-shadow: 0 18px 44px -14px rgba(52,211,153,0.75), 0 0 0 1px rgba(52,211,153,0.6); background-color: #4ADEAA; }
  [data-cta="light"]:not([aria-disabled="true"]):hover { box-shadow: 0 18px 44px -14px rgba(165,180,252,0.6); background-color: #FFFFFF; }
  [data-cta="ghost"]:not([aria-disabled="true"]):hover { background-color: rgba(255,255,255,0.10); border-color: rgba(52,211,153,0.55); }
}
[data-cta]:not([aria-disabled="true"]):active { transform: translate3d(var(--bo-tx, 0px), var(--bo-ty, 0px), 0) scale(0.97); transition-duration: 90ms; }
@keyframes boCtaShine { 0% { opacity: 1; transform: translateX(-130%) skewX(-18deg); } 100% { opacity: 1; transform: translateX(300%) skewX(-18deg); } }
@media (prefers-reduced-motion: reduce) {
  [data-cta], [data-cta-label], [data-cta-arrow], [data-cta-icon] { transition: background-color 200ms, border-color 200ms !important; transform: none !important; }
  [data-cta-shine] { display: none; } [data-cta-label="b"] { display: none; }
}
`;

const PULL = 0.28;   // share of the pointer offset the button follows
const MAX_PULL = 10; // px

const INK = { primary: '#0B0B12', light: '#0B0B12', ghost: 'rgba(255,255,255,0.94)' };

export default function MagneticButton({
    label,
    onPress,
    variant = 'primary',
    icon,
    arrow = true,
    busy = false,
    busyLabel,
    disabled = false,
    style,
    textStyle,
    accessibilityLabel,
    dataSet,
    ...rest
}) {
    const ref = useRef(null);
    const reduced = useReducedMotion();
    const fx = IS_WEB && !reduced;
    const ink = INK[variant] || INK.primary;
    const off = disabled || busy;
    // Inject while rendering so the label copies are positioned from the first paint.
    if (IS_WEB) injectCss('magnetic-button', CSS);

    useEffect(() => {
        if (!IS_WEB) return undefined;
        const node = ref.current;
        if (!fx || !node?.addEventListener || typeof window === 'undefined' || !window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return undefined;
        const clamp = (v) => Math.max(-MAX_PULL, Math.min(MAX_PULL, v));
        const move = (e) => {
            if (node.getAttribute('aria-disabled') === 'true') return;
            const r = node.getBoundingClientRect();
            node.style.setProperty('--bo-tx', `${clamp((e.clientX - (r.left + r.width / 2)) * PULL)}px`);
            node.style.setProperty('--bo-ty', `${clamp((e.clientY - (r.top + r.height / 2)) * PULL)}px`);
        };
        const leave = () => { node.style.setProperty('--bo-tx', '0px'); node.style.setProperty('--bo-ty', '0px'); };
        node.addEventListener('pointermove', move, { passive: true });
        node.addEventListener('pointerleave', leave);
        return () => { node.removeEventListener('pointermove', move); node.removeEventListener('pointerleave', leave); };
    }, [fx]);

    const labelText = <Text style={[styles.text, { color: ink }, textStyle]} numberOfLines={1}>{label}</Text>;
    const content = busy ? (
        <>
            <ActivityIndicator size="small" color={ink} />
            {!!busyLabel && <Text style={[styles.text, { color: ink }, textStyle]} numberOfLines={1}>{busyLabel}</Text>}
        </>
    ) : (
        <>
            {!!icon && (
                <View {...(IS_WEB ? { dataSet: { ctaIcon: '' } } : {})}>
                    <Ionicons name={icon} size={18} color={ink} />
                </View>
            )}
            {fx ? (
                <View dataSet={{ ctaRoll: '' }}>
                    <Text style={[styles.text, { color: ink }, textStyle]} numberOfLines={1} dataSet={{ ctaLabel: 'a' }}>{label}</Text>
                    <Text style={[styles.text, { color: ink }, textStyle]} numberOfLines={1} dataSet={{ ctaLabel: 'b' }} aria-hidden>{label}</Text>
                </View>
            ) : labelText}
            {arrow && (
                <View {...(IS_WEB ? { dataSet: { ctaArrow: '' } } : {})}>
                    <Ionicons name="arrow-forward" size={17} color={ink} />
                </View>
            )}
        </>
    );

    const a11y = {
        accessibilityRole: 'button',
        accessibilityLabel: accessibilityLabel || label,
        accessibilityState: { disabled: !!off, busy: !!busy },
    };

    if (!IS_WEB) {
        return (
            <PressableScale onPress={onPress} disabled={off} style={[styles.base, styles[variant], style]} {...a11y} {...rest}>
                {content}
            </PressableScale>
        );
    }

    return (
        <Pressable
            ref={ref}
            onPress={onPress}
            disabled={off}
            dataSet={{ ...dataSet, cta: variant }}
            aria-busy={!!busy}
            style={[styles.base, styles[variant], off && styles.off, style]}
            {...a11y}
            {...rest}
        >
            {fx && <View style={styles.shineBox} dataSet={{ ctaShine: '' }} aria-hidden />}
            {content}
        </Pressable>
    );
}

const styles = StyleSheet.create({
    base: { minHeight: 48, borderRadius: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 9, paddingHorizontal: 22 },
    primary: { backgroundColor: '#34D399' },
    light: { backgroundColor: '#F5F5FA' },
    ghost: { backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.16)' },
    off: { opacity: 0.45, ...(Platform.OS === 'web' ? { cursor: 'default' } : {}) },
    shineBox: { position: 'absolute', pointerEvents: 'none' },
    text: { fontFamily: Platform.OS === 'web' ? 'Slate, -apple-system, system-ui, "Segoe UI", sans-serif' : undefined, fontSize: 15, fontWeight: '700' },
});
